<template>
  <div class="layout-container">
    <div class="sidebar">
      <div class="logo">
        <img v-if="sysConfig.system_logo" :src="sysConfig.system_logo" class="logo-img" alt="LOGO" />
        <span class="logo-text">{{ sysConfig.system_name || '粮库资产管理平台' }}</span>
      </div>
      <div class="menu-scroll">
        <el-menu :default-active="activeMenu" class="el-menu-vertical-demo" @select="handleMenuSelect">
        <template v-for="menu in menuList" :key="menu.id">
          <el-sub-menu v-if="menu.children && menu.children.length > 0" :index="menu.path">
            <template #title>
              <el-icon><component :is="getIcon(menu.icon)" /></el-icon>
              <span>{{ menu.name }}</span>
            </template>
            <el-menu-item 
              v-for="child in menu.children" 
              :key="child.id" 
              :index="child.path">
              <el-icon><component :is="getIcon(child.icon)" /></el-icon>
              <span>{{ child.name }}</span>
            </el-menu-item>
          </el-sub-menu>
          <el-menu-item v-else :index="menu.path">
            <el-icon><component :is="getIcon(menu.icon)" /></el-icon>
            <span>{{ menu.name }}</span>
          </el-menu-item>
        </template>
      </el-menu>
      </div>
    </div>
    <div class="main-content">
      <div class="header">
        <div class="header-left">
          <el-breadcrumb separator="/">
            <el-breadcrumb-item :to="{ path: '/index' }">首页</el-breadcrumb-item>
            <el-breadcrumb-item>{{ currentPageName }}</el-breadcrumb-item>
          </el-breadcrumb>
        </div>
        <div class="header-right">
          <!-- 资产大屏入口：放在用户信息左侧 -->
          <el-button class="dash-entry" :icon="TrendCharts" @click="goDashboard">资产大屏</el-button>
          <div class="header-divider"></div>
          <el-dropdown>
            <span class="el-dropdown-link user-info">
              <div class="user-avatar">
                <el-icon :size="24"><User /></el-icon>
              </div>
              <div class="user-detail">
                <div class="user-name">{{ userInfo?.realName || userInfo?.username || '管理员' }}</div>
                <div class="user-role">{{ getRoleName() }}</div>
              </div>
              <el-icon class="el-icon--right"><ArrowDown /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="handleLogout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
      <div class="content">
        <router-view />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { getMenuByRoleId, getMenuList } from '../api'
import { 
  Menu, FolderOpened, Bell, Setting, Document, Link, Monitor, HomeFilled, User, ArrowDown,
  List, Search, Goods, OfficeBuilding, VideoCamera, AlarmClock, Lock, TrendCharts, Connection
} from '@element-plus/icons-vue'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'
import { sysConfig } from '../utils/sysConfig'

const router = useRouter()
const route = useRoute()
const activeMenu = ref('/index')
const userInfo = ref(null)
const menus = ref([])

const pageNameMap = {
  '/index': '首页',
  '/workorder': '工单管理',
  '/patrol': '巡检管理',
  '/contract': '合同管理',
  '/grain-store': '粮库配置',
  '/warehouse': '仓房管理',
  '/monitor': '监控管理',
  '/monitor-plan': '监控平面标注',
  '/alarm': '报警管理',
  '/dept': '部门管理',
  '/user': '用户管理',
  '/menu': '菜单管理',
  '/permission': '权限管理',
  '/system': '系统监控',
  '/report': '报表管理',
  '/integration': '系统集成',
  '/store-role': '粮库岗位',
  '/log': '日志管理'
}

const currentPageName = computed(() => {
  // 优先从后台菜单配置取名称，菜单改名后面包屑自动同步
  const findMenuName = (list) => {
    if (!list) return null
    for (const m of list) {
      if (m.path === route.path) return m.name
      const found = findMenuName(m.children)
      if (found) return found
    }
    return null
  }
  return findMenuName(menuList.value) || pageNameMap[route.path] || ''
})

const getRoleName = () => {
  const roleMap = {
    1: '超级管理员',
    2: '粮库管理员',
    3: '仓库管理员',
    4: '巡检人员',
    5: '财务人员',
    6: '普通用户'
  }
  return roleMap[userInfo.value?.roleId] || '用户'
}

const getIcon = (iconName) => {
  if (!iconName) return FolderOpened
  return ElementPlusIconsVue[iconName] || FolderOpened
}

const menuList = computed(() => {
  // 仓房管理 / 监控管理已合并进「粮库管理」页内以弹出层管理，左侧菜单只保留粮库管理
  // 资产大屏入口改到右上角，不再注入左侧菜单
  const HIDDEN_PATHS = ['/warehouse', '/monitor', '/monitor-plan', '/dashboard']
  const visibleMenus = menus.value.filter(m => !HIDDEN_PATHS.includes(m.path || ''))

  const tree = []
  const map = new Map()
  visibleMenus.forEach(menu => {
    const path = menu.path || `/menu-${menu.id}`
    map.set(menu.id, { ...menu, name: menu.menuName, path: path, children: [] })
  })
  visibleMenus.forEach(menu => {
    if (menu.parentId !== 0) {
      const parent = map.get(menu.parentId)
      if (parent) {
        const path = menu.path || `/menu-${menu.id}`
        parent.children.push({ ...menu, name: menu.menuName, path: path })
      }
    }
  })
  map.forEach(menu => {
    if (menu.parentId === 0) {
      tree.push(menu)
    }
  })
  // 子项被隐藏后，若父级已无可见子项则一并隐藏（仅当原本身为父级时）
  const cleaned = tree.filter(parent =>
    !parent.children || parent.children.length > 0 || !HIDDEN_PATHS.includes(parent.path || '')
  )
  return cleaned.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
})

onMounted(() => {
  const user = localStorage.getItem('user')
  if (user) {
    userInfo.value = JSON.parse(user)
  }
  activeMenu.value = route.path
  loadMenus()
})

const loadMenus = async () => {
  try {
    const user = localStorage.getItem('user')
    if (user) {
      const userData = JSON.parse(user)
      if (userData.roleId) {
        const res = await getMenuByRoleId(userData.roleId)
        menus.value = res.data
        return
      }
    }
    const res = await getMenuList()
    menus.value = res.data
  } catch (error) {
    console.error('加载菜单失败', error)
  }
}

watch(() => route.path, (newPath) => {
  activeMenu.value = newPath
})

const handleMenuSelect = (index) => {
  router.push(index)
}

const goDashboard = () => {
  window.open('/dashboard', '_blank')
}

const handleLogout = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('user')
  ElMessage.success('退出成功')
  router.push('/login')
}
</script>

<style scoped>
.layout-container {
  display: flex;
  height: 100vh;
}

.sidebar {
  width: 220px;
  background: #2a3f5f;
  color: white;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.logo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 18px 12px;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  background: #1a2744;
  flex-shrink: 0;
}

.logo-img {
  width: 30px;
  height: 30px;
  object-fit: contain;
  border-radius: 4px;
}

.logo-text {
  white-space: nowrap;
}

.menu-scroll {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
  height: 64px;
  background: #2a3f5f;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.header-left {
  flex: 1;
}

.header-left :deep(.el-breadcrumb__item) {
  font-size: 14px;
}

.header-left :deep(.el-breadcrumb__inner) {
  color: rgba(255, 255, 255, 0.9);
}

.header-left :deep(.el-breadcrumb__inner a) {
  color: rgba(255, 255, 255, 0.7);
}

.header-left :deep(.el-breadcrumb__separator) {
  color: rgba(255, 255, 255, 0.5);
  margin: 0 8px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.dash-entry {
  background: rgba(64, 158, 255, 0.18);
  border: 1px solid rgba(64, 158, 255, 0.55);
  color: #cfe2ff;
  transition: all 0.3s ease;
}
.dash-entry:hover {
  background: rgba(64, 158, 255, 0.3);
  color: #fff;
}

.header-divider {
  width: 1px;
  height: 32px;
  background: rgba(255, 255, 255, 0.3);
}

.el-dropdown-link {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.user-info {
  color: white;
  padding: 8px 12px;
  border-radius: 8px;
  transition: background 0.3s ease;
}

.user-info:hover {
  background: rgba(255, 255, 255, 0.15);
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}

.user-detail {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
}

.user-role {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.2;
}

.content {
  flex: 1;
  padding: 20px;
  background: #f5f5f5;
  overflow-y: auto;
  position: relative;
}

:deep(.el-menu-vertical-demo) {
  background-color: #2a3f5f !important;
  border-right: 0px !important;
}

:deep(.el-menu-vertical-demo .el-menu-item) {
  color: #ffffff !important;
  background-color: #2a3f5f !important;
}

:deep(.el-menu-vertical-demo .el-menu-item:hover) {
  background-color: #1a2744 !important;
  color: #ffffff !important;
}

:deep(.el-menu-vertical-demo .el-menu-item.is-active) {
  background-color: #409eff !important;
  color: #ffffff !important;
}

:deep(.el-menu-vertical-demo .el-sub-menu__title) {
  color: #ffffff !important;
  background-color: #2a3f5f !important;
}

:deep(.el-menu-vertical-demo .el-sub-menu__title:hover) {
  background-color: #1a2744 !important;
  color: #ffffff !important;
}

:deep(.el-menu-vertical-demo .el-sub-menu.is-active .el-sub-menu__title) {
  background-color: #2a3f5f !important;
  color: #ffffff !important;
}

:deep(.el-menu--popup) {
  background-color: #2a3f5f !important;
}

:deep(.el-menu--popup .el-menu-item) {
  color: #ffffff !important;
  background-color: #2a3f5f !important;
}

:deep(.el-menu--popup .el-menu-item:hover) {
  background-color: #1a2744 !important;
  color: #ffffff !important;
}

:deep(.el-menu--popup .el-menu-item.is-active) {
  background-color: #409eff !important;
  color: #ffffff !important;
}

.menu-scroll::-webkit-scrollbar {
  width: 6px;
}

.menu-scroll::-webkit-scrollbar-track {
  background: #1a2744;
}

.menu-scroll::-webkit-scrollbar-thumb {
  background: #4a6584;
  border-radius: 3px;
}

.menu-scroll::-webkit-scrollbar-thumb:hover {
  background: #6a85a4;
}

.menu-scroll::-webkit-scrollbar-corner {
  background: #1a2744;
}

.menu-scroll {
  -ms-overflow-style: none;
  scrollbar-width: thin;
  scrollbar-color: #4a6584 #1a2744;
}
</style>
